import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const normComparisonComponent: ComponentDef = {
  id: 'norm-comparison',
  name: 'Norm Comparison',
  description: 'Compare LayerNorm, RMSNorm, and BatchNorm on the same hidden states.',
  category: 'function',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'variant', label: 'Variant', type: 'select', default: 'overview', options: [
      { label: 'Overview', value: 'overview' },
      { label: 'Normalization Dimensions', value: 'norm_dims' },
      { label: 'Training Dynamics', value: 'training_dynamics' }
    ]}
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant || 'overview');

    const g = new GraphBuilder();

    if (variant === 'overview') {
      // ── OVERVIEW: LayerNorm + RMSNorm + BatchNorm in parallel ──
      g.input('x', [seq_len, d_model]);

      g.norm('layernorm', 'x', 'LayerNorm');
      g.norm('rmsnorm', 'x', 'RMSNorm');
      g.norm('batchnorm', 'x', 'BatchNorm');

      g.concat('out_cat', ['layernorm', 'rmsnorm', 'batchnorm'], [seq_len, d_model * 3]);
      g.output('out', 'out_cat');

    } else if (variant === 'norm_dims') {
      // ── NORM DIMS: show how each norm normalizes along different axes ──
      g.input('x', [seq_len, d_model]);

      // LayerNorm: normalizes along d_model (last axis) per position
      // Reshape to highlight the reduction axis, then norm
      g.reshape('ln_view', 'x', [seq_len, d_model]);
      g.activation('ln_mean', 'ln_view', 'mean over d_model (per position)');
      g.activation('ln_centered', 'ln_mean', 'x − mean');
      g.activation('ln_var', 'ln_centered', 'var over d_model');
      g.norm('ln_out', 'ln_var', 'LayerNorm (axis=d_model, mean+var)');

      // RMSNorm: normalizes along d_model (no mean centering, only RMS)
      g.reshape('rn_view', 'x', [seq_len, d_model]);
      g.activation('rn_rms', 'rn_view', 'RMS over d_model (no mean centering)');
      g.norm('rn_out', 'rn_rms', 'RMSNorm (axis=d_model, RMS only)');

      // BatchNorm: normalizes along batch/seq dimension per feature
      // Reshape to highlight the batch reduction axis
      g.reshape('bn_view', 'x', [seq_len, d_model]);
      g.transpose('bn_transposed', 'bn_view', [d_model, seq_len], 'transpose → [d_model, seq_len]');
      g.activation('bn_mean', 'bn_transposed', 'mean over batch/seq (per feature)');
      g.activation('bn_centered', 'bn_mean', 'x − batch_mean');
      g.activation('bn_var', 'bn_centered', 'var over batch/seq');
      g.transpose('bn_back', 'bn_var', [seq_len, d_model], 'transpose back → [seq_len, d_model]');
      g.norm('bn_out', 'bn_back', 'BatchNorm (axis=batch, mean+var)');

      // Compare outputs
      g.concat('dims_out', ['ln_out', 'rn_out', 'bn_out'], [seq_len, d_model * 3]);
      g.output('out', 'dims_out');

    } else {
      // ── TRAINING DYNAMICS: norm behavior during training ──
      // Show a mini training loop: input -> norm -> linear -> activation -> dropout -> norm again
      g.input('x', [seq_len, d_model]);
      g.input('targets', [seq_len]);

      // First norm step
      g.norm('ln_step1', 'x', 'LayerNorm (per-sample stats)');
      g.norm('rn_step1', 'x', 'RMSNorm (per-sample RMS)');
      g.norm('bn_step1', 'x', 'BatchNorm (running mean/var)');

      // Linear + activation + dropout after each norm
      g.linear('ln_linear', 'ln_step1', [d_model, d_model]);
      g.activation('ln_act', 'ln_linear', 'GELU');
      g.dropout('ln_drop', 'ln_act', 0.1);

      g.linear('rn_linear', 'rn_step1', [d_model, d_model]);
      g.activation('rn_act', 'rn_linear', 'GELU');
      g.dropout('rn_drop', 'rn_act', 0.1);

      g.linear('bn_linear', 'bn_step1', [d_model, d_model]);
      g.activation('bn_act', 'bn_linear', 'GELU');
      g.dropout('bn_drop', 'bn_act', 0.1);

      // Second norm step (showing how stats differ)
      g.norm('ln_step2', 'ln_drop', 'LayerNorm (per-sample stats, step 2)');
      g.norm('rn_step2', 'rn_drop', 'RMSNorm (per-sample RMS, step 2)');
      g.norm('bn_step2', 'bn_drop', 'BatchNorm (updated running mean/var, step 2)');

      // Show running statistics tracking for BatchNorm
      g.scale('bn_running_mean', 'bn_step2', 'running_mean = α·batch_mean + (1−α)·old_mean');
      g.scale('bn_running_var', 'bn_step2', 'running_var = α·batch_var + (1−α)·old_var');

      // Show per-sample stat computation for LayerNorm/RMSNorm
      g.activation('ln_per_sample', 'ln_step2', 'LayerNorm: μ,σ² computed per-sample (independent of batch)');
      g.activation('rn_per_sample', 'rn_step2', 'RMSNorm: RMS computed per-sample (no mean, no batch dep)');

      // Output showing all norm paths after 2 steps
      g.concat('training_out', ['ln_per_sample', 'rn_per_sample', 'bn_running_mean'], [seq_len, d_model * 3]);
      g.output('out', 'training_out');
    }

    return g.build();
  }
};
